import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  LoginByGBizIdoAuth2Mutation,
  LoginByGBizIdoAuth2MutationVariables,
  LoginByGoogleOAuth2Mutation,
  LoginByGoogleOAuth2MutationVariables,
  LoginByLineOAuth2Mutation,
  LoginByLineOAuth2MutationVariables,
  LoginByLineTokenMutation,
  LoginByLineTokenMutationVariables,
  LoginMutation,
  LoginMutationVariables,
  LogoutMutation,
  LogoutMutationVariables,
  UserQuery,
} from "./types";
import { useCallback } from "react";

export const USER_FRAGMENT = gql`
  fragment UserFragment on User {
    id
    email
    lineId
    googleId
    firstName
    lastName
    createdAt
    twoPhaseAuthSetting
  }
`;

export const USER = gql`
  query User {
    user {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

const LOGIN = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      ... on LoginSuccess {
        user {
          ...UserFragment
        }
        token
      }

      ... on TemporaryAuth {
        noop
      }

      ... on LoginError {
        error
      }
    }
  }
  ${USER_FRAGMENT}
`;

const LINE_LOGIN_TOKEN = gql`
  mutation LoginByLineToken($token: String!) {
    loginByLineToken(token: $token) {
      ... on LineLoginSuccess {
        user {
          ...UserFragment
        }
        token
      }

      ... on LineLoginError {
        error
      }
    }
  }
  ${USER_FRAGMENT}
`;

const LINE_LOGIN_OAUTH2 = gql`
  mutation LoginByLineOAuth2($input: LoginByLineOAuth2Input!) {
    loginByLineOAuth2(input: $input) {
      ... on LoginByLineOAuth2SignUp {
        lineSignupSessionId
        lineSignupSessionEmail
      }

      ... on LoginByLineOAuth2Login {
        token
        user {
          ...UserFragment
        }
      }

      ... on LoginByLineOAuth2TemporaryAuth {
        noop
      }
    }
  }
  ${USER_FRAGMENT}
`;

const GBIZID_LOGIN_OAUTH2 = gql`
  mutation LoginByGBizIDOAuth2($input: LoginByGBizIDOAuth2Input!) {
    loginByGBizIDOAuth2(input: $input) {
      ... on LoginByGBizIDOAuth2SignUp {
        gBizIDSignupSessionId
        gBizIDSignupSessionEmail
      }

      ... on LoginByGBizIDOAuth2Login {
        token
        user {
          ...UserFragment
        }
      }

      ... on LoginByGBizIDOAuth2TemporaryAuth {
        noop
      }
    }
  }
  ${USER_FRAGMENT}
`;

const GOOGLE_LOGIN_OAUTH2 = gql`
  mutation LoginByGoogleOAuth2($code: String!) {
    loginByGoogleOAuth2(code: $code) {
      ... on LoginByGoogleOAuth2Success {
        type
        token
        user {
          ...UserFragment
        }
      }

      ... on LoginByGoogleOAuth2TemporaryAuth {
        noop
      }

      ... on LoginByGoogleOAuth2Error {
        error
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const LOGOUT = gql`
  mutation Logout {
    logout {
      id
    }
  }
`;

// Query
export const useUserQuery = () => useQuery<UserQuery>(USER);
export const useLazyUserQuery = () => useLazyQuery<UserQuery>(USER);

// Mutation
// MutationはApolloキャッシュの同期の作業があるので、utils/auth-hooksにあるものを使って
// こちらはアプリケーションから直接は使わない
export const useLoginMutation = () =>
  useMutation<LoginMutation, LoginMutationVariables>(LOGIN);
export const useLineLoginTokenMutation = () =>
  useMutation<LoginByLineTokenMutation, LoginByLineTokenMutationVariables>(
    LINE_LOGIN_TOKEN,
  );
export const useLineLoginOAuth2Mutation = () =>
  useMutation<LoginByLineOAuth2Mutation, LoginByLineOAuth2MutationVariables>(
    LINE_LOGIN_OAUTH2,
  );
export const useGBizIDLoginOAuth2Mutation = () =>
  useMutation<
    LoginByGBizIdoAuth2Mutation,
    LoginByGBizIdoAuth2MutationVariables
  >(GBIZID_LOGIN_OAUTH2);
export const useGoogleLoginOAuth2Mutation = () =>
  useMutation<
    LoginByGoogleOAuth2Mutation,
    LoginByGoogleOAuth2MutationVariables
  >(GOOGLE_LOGIN_OAUTH2);
export const useLogoutMutation = () =>
  useMutation<LogoutMutation, LogoutMutationVariables>(LOGOUT);

export const useFetchUser = () => {
  const client = useApolloClient();

  return useCallback(() => client.query<UserQuery>({ query: USER }), [client]);
};
